import React from 'react';
import numeral from 'numeral';
import { ErrorsBlockComponent } from '../../shared/errorsBlockComponent';
import { ButtonBusyIndicatorComponent } from '../../shared/buttonBusyIndicatorComponent';
import { MakeApiCall, ReplaceUrl } from '../../shared/utils';
import { SimpleLineChart, NormalizeDataTable } from '../../shared/Charts';

const RequestsCountChartOptions = {
    lineWidth: 1,
    pointsVisible: false,
    backgroundColor: 'transparent',
    legend: { position: 'right' },
    series: {
        0: { color: '#396caa' }, 
        1: { color: '#eb5757' },
    },
    tooltip: { ignoreBounds: true, trigger: 'focus' },
}

const RequestDurationChartOptions = {
    lineWidth: 1,
    pointsVisible: false,
    backgroundColor: 'transparent',
    legend: { position: 'none' },
    series: {
        0: {color: '#396caa'}
    },
    tooltip: { ignoreBounds: true, trigger: 'focus' },
}

export class ServiceDashboardComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);

        this.state = {
            requestTypeId: props.requestTypes.some(p => p.Id == props.requestTypeId) ? props.requestTypeId : props.requestTypes[0].Id,
            requestStats: {
                requestType: null,
                isLoading: false,
                isLoaded: false,
                data: null,
                errors: null,
            },
            location: {
                pathname: location.pathname.replace(/\/[0-9]*$/, ''),
                hash: location.hash, 
                search: location.search
            },            
        };

        this.handleRequestTypeChange = this.handleRequestTypeChange.bind(this);
        this.doRequestStatsLoad = this.doRequestStatsLoad.bind(this);
    }

    componentDidMount() {
        const self = this;
        self._isMounted = true;

        if (self.state.requestTypeId != self.props.requestTypes[0].Id) {
            self.doRequestStatsLoad();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    handleRequestTypeChange(e) {
        this.setState({requestTypeId: e.target.value});
    }

    doRequestStatsLoad() {
        const self = this;

        if (!self._isMounted) {
            return;
        }

        const requestTypeId = self.state.requestTypeId;
        const requestType = {...self.props.requestTypes.find(p => p.Id == requestTypeId)};
        const setRequestStats = (stats, callback) => {
            self.setState({requestStats: {...self.state.requestStats, ...stats}}, callback);
        }

        new Promise(resolve => {
            setRequestStats({requestType: requestType, isLoading: true, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getServiceRequestStats',
                            data: {requestTypeId: requestTypeId},
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                const data = responseData;

                data.Last1Year = NormalizeDataTable(data.Last1Year);
                data.Last24Hours = NormalizeDataTable(data.Last24Hours);
                data.Last4Weeks = NormalizeDataTable(data.Last4Weeks);
                const pathname = self.state.location.pathname + (requestTypeId == 0 ? '' : `/${requestTypeId}`);

                setRequestStats(
                    {requestType: requestType, isLoaded: true, isLoading: false, data: responseData},
                    () => ReplaceUrl({...self.state.location, pathname}));
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                setRequestStats({requestType: requestType, isLoaded: false, isLoading: false, errors: errors});
            }
        });
    }

    render() {
        const self = this;
        const {requestTypes} = self.props;
        const {requestTypeId, requestStats} = self.state;

        let totalCount24h = 0
        let failedCount24h = 0;
        let duration24h = 0;
        let totalCount4w = 0
        let failedCount4w = 0;
        let duration4w = 0;
        let totalCount1y = 0
        let failedCount1y = 0;
        let duration1y = 0;


        if (requestStats.isLoaded) {
            requestStats.data.Last24Hours.rows.forEach(r => {
                totalCount24h += r.c[1].v;
                failedCount24h += r.c[2].v;
                duration24h += r.c[3].v * r.c[1].v;
            });

            requestStats.data.Last4Weeks.rows.forEach(r => {
                totalCount4w += r.c[1].v;
                failedCount4w += r.c[2].v;
                duration4w += r.c[3].v * r.c[1].v;
            });

            requestStats.data.Last1Year.rows.forEach(r => {
                totalCount1y += r.c[1].v;
                failedCount1y += r.c[2].v;
                duration1y += r.c[3].v * r.c[1].v;
            });
        }

        const averageDuration24h = totalCount24h > 0 ? duration24h / totalCount24h : 0;
        const averageDuration4w = totalCount4w > 0 ? duration4w / totalCount4w : 0;
        const averageDuration1y = totalCount1y > 0 ? duration1y / totalCount1y : 0;

        return (
            <div>
                <div>
                    <div className="input-group newstyle">
                        <select 
                            className="custom-select"
                            disabled={requestStats.isLoading}
                            value={requestTypeId}
                            onChange={self.handleRequestTypeChange}
                        >
                            {requestTypes.map(p => <option key={p.Id} value={p.Id}>{p.Name}</option>)}
                        </select>
                        <div className="input-group-append">
                            <button
                                className="btn btn-outline-secondary newstyle"
                                disabled={requestStats.isLoading}
                                onClick={self.doRequestStatsLoad}
                            >
                                Get
                                <ButtonBusyIndicatorComponent visible={requestStats.isLoading} />
                            </button>
                        </div>
                    </div>
                    <div>
                        <ErrorsBlockComponent errors={requestStats.errors} unstyled />
                        {requestStats.isLoaded &&
                            <>
                                <div className="pt-3">
                                    <h3>Last 24 hours (step 5 min)</h3>
                                    <div className="pt-1 pb-2">
                                        <h4>Requests count</h4>
                                        <div className="py-1 fs-sm text-secondary">
                                            <div>Total count: {numeral(totalCount24h).format('0,0[.]0')}</div>
                                            {(failedCount24h > 0) &&
                                                <div className="text-danger">
                                                    Failed count: {numeral(failedCount24h).format('0,0[.]0')} ({numeral(100.0 * failedCount24h / totalCount24h).format('0.00')}%)
                                                </div>
                                            }
                                        </div>
                                        <SimpleLineChart 
                                            dataTable={requestStats.data.Last24Hours}
                                            options={RequestsCountChartOptions}
                                            hiddenColumns={[3]}
                                            toggleColumnVisibleOnLegendClick
                                        />
                                    </div>
                                    <div className="pt-1 pb-2">
                                        <h4>Request duration, ms</h4>
                                        {(averageDuration24h > 0) &&
                                            <div className="py-1 fs-sm text-secondary">
                                                Average duration, ms: {numeral(averageDuration24h).format('0,0[.]0')}
                                            </div>
                                        }
                                        <SimpleLineChart 
                                            dataTable={requestStats.data.Last24Hours}
                                            options={RequestDurationChartOptions}
                                            hiddenColumns={[1, 2]}
                                        />
                                    </div>
                                </div>
                                <div className="pt-3">
                                    <h3>Last 4 weeks (step 2 h)</h3>
                                    <div className="pt-1 pb-2">
                                        <h4>Requests count</h4>
                                        <div className="py-1 fs-sm text-secondary">
                                            <div>Total count: {numeral(totalCount4w).format('0,0[.]0')}</div>
                                            {(failedCount4w > 0) &&
                                                <div className="text-danger">
                                                    Failed count: {numeral(failedCount4w).format('0,0[.]0')} ({numeral(100.0 * failedCount4w / totalCount4w).format('0.00')}%)
                                                </div>
                                            }
                                        </div>
                                        <SimpleLineChart 
                                            dataTable={requestStats.data.Last4Weeks}
                                            options={RequestsCountChartOptions}
                                            hiddenColumns={[3]}
                                            toggleColumnVisibleOnLegendClick
                                        />
                                    </div>
                                    <div className="pt-1 pb-2">
                                        <h4>Request duration, ms</h4>
                                        {(averageDuration4w > 0) &&
                                            <div className="py-1 fs-sm text-secondary">
                                                Average duration, ms: {numeral(averageDuration4w).format('0,0[.]0')}
                                            </div>
                                        }
                                        <SimpleLineChart 
                                            dataTable={requestStats.data.Last4Weeks}
                                            options={RequestDurationChartOptions}
                                            hiddenColumns={[1, 2]}
                                        />
                                    </div>
                                </div>
                                <div className="pt-3">
                                    <h3>Last 1 year (step 24 h)</h3>
                                    <div className="pt-1 pb-2">
                                        <h4>Requests count</h4>
                                        <div className="py-1 fs-sm text-secondary">
                                            <div>Total count: {numeral(totalCount1y).format('0,0[.]0')}</div>
                                            {(failedCount1y > 0) &&
                                                <div className="text-danger">
                                                    Failed count: {numeral(failedCount1y).format('0,0[.]0')} ({numeral(100.0 * failedCount1y / totalCount1y).format('0.00')}%)
                                                </div>
                                            }
                                        </div>
                                        <SimpleLineChart 
                                            dataTable={requestStats.data.Last1Year}
                                            options={RequestsCountChartOptions}
                                            hiddenColumns={[3]}
                                            toggleColumnVisibleOnLegendClick
                                        />
                                    </div>
                                    <div className="pt-1 pb-2">
                                        <h4>Request duration, ms</h4>
                                        {(averageDuration1y > 0) &&
                                            <div className="py-1 fs-sm text-secondary">
                                                Average duration, ms: {numeral(averageDuration1y).format('0,0[.]0')}
                                            </div>
                                        }
                                        <SimpleLineChart 
                                            dataTable={requestStats.data.Last1Year}
                                            options={RequestDurationChartOptions}
                                            hiddenColumns={[1, 2]}
                                        />
                                    </div>
                                </div>
                            </>
                        }
                    </div>
                </div>
            </div>
        )
    }
}